/**
 * Templates de messages WhatsApp prédéfinis liés aux commandes.
 *
 * Chaque template est écrit avec des variables au format {{cle}} qui sont
 * remplacées à l'envoi par les valeurs réelles (voir utils/messageTemplate.js).
 *
 * Pour ajouter un nouveau message prédéfini : ajouter une entrée dans
 * TEMPLATES_MESSAGES_COMMANDE. Aucun autre fichier n'a besoin d'être modifié.
 */

import { getDeviseActive } from '../utils/devise';

/**
 * Liste des variables disponibles pour les templates de commande.
 * Sert uniquement à l'affichage d'aide dans l'interface d'édition.
 */
export const VARIABLES_COMMANDE = [
  { cle: 'client', description: 'Nom du client' },
  { cle: 'telephone', description: 'Téléphone du client' },
  { cle: 'numero_commande', description: 'Numéro de la commande' },
  { cle: 'montant', description: 'Montant total formaté' },
  { cle: 'adresse', description: 'Adresse de livraison' },
  { cle: 'date_livraison', description: 'Date de livraison prévue' },
  { cle: 'produits', description: 'Liste des articles commandés' },
  { cle: 'livreur', description: 'Nom du livreur assigné' }
];

/**
 * Templates prédéfinis pour les commandes.
 * - cle    : identifiant unique du template
 * - libelle: nom affiché dans la liste de sélection
 * - resume : courte description du cas d'usage
 * - contenu: corps du message avec variables {{cle}}
 */
export const TEMPLATES_MESSAGES_COMMANDE = [
  {
    cle: 'numero_injoignable',
    libelle: 'Numéro injoignable',
    resume: 'Le numéro du client ne passe pas',
    contenu:
      'Bonjour {{client}} 👋\n\n' +
      'Nous avons tenté de vous joindre au {{telephone}} au sujet de votre commande {{numero_commande}}, ' +
      'mais votre numéro ne passe pas.\n\n' +
      'Merci de nous répondre directement ici sur WhatsApp ou de nous communiquer un autre numéro joignable ' +
      'afin que nous puissions confirmer et livrer votre commande.\n\n' +
      'À très vite ! 😊'
  },
  {
    cle: 'appel_sans_reponse',
    libelle: 'Appel sans réponse',
    resume: 'Le client a été appelé mais n\'a pas répondu',
    contenu:
      'Bonjour {{client}} 👋\n\n' +
      'Nous vous avons appelé concernant votre commande {{numero_commande}} mais nous n\'avons pas pu vous joindre.\n\n' +
      'Merci de nous confirmer ici :\n' +
      '• Que vous maintenez votre commande\n' +
      '• Votre adresse de livraison : {{adresse}}\n' +
      '• Un créneau où vous êtes disponible\n\n' +
      'Nous restons à votre disposition 🙏'
  },
  {
    cle: 'confirmation_commande',
    libelle: 'Confirmation de commande',
    resume: 'Récapitulatif et demande de confirmation',
    contenu:
      'Bonjour {{client}} 👋\n\n' +
      'Voici le récapitulatif de votre commande {{numero_commande}} :\n\n' +
      '{{produits}}\n\n' +
      '💰 Montant total : {{montant}}\n' +
      '📍 Adresse de livraison : {{adresse}}\n' +
      '📅 Livraison prévue : {{date_livraison}}\n\n' +
      'Merci de nous confirmer ces informations en répondant à ce message ✅'
  },
  {
    cle: 'livraison_du_jour',
    libelle: 'Livraison en cours',
    resume: 'Prévenir le client du passage du livreur',
    contenu:
      'Bonjour {{client}} 👋\n\n' +
      'Votre commande {{numero_commande}} est prévue pour la livraison ({{date_livraison}}) ' +
      'à l\'adresse suivante : {{adresse}}.\n\n' +
      'Notre livreur {{livreur}} vous contactera au {{telephone}} avant son passage.\n\n' +
      'Merci de rester joignable 🙏'
  }
];

/**
 * Formate un montant dans la devise de la boutique active (XOF affiché avec le symbole court « F »).
 *
 * @param {number} montant - Montant brut
 * @returns {string} Montant formaté (ex: "15 000 F")
 */
const formaterMontant = (montant) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: getDeviseActive(),
    minimumFractionDigits: 0
  })
    .format(montant || 0)
    .replace('XOF', 'F');
};

/**
 * Construit le dictionnaire de variables à partir d'une commande.
 * Les valeurs manquantes reçoivent un libellé neutre pour éviter les trous
 * dans le message envoyé au client.
 *
 * @param {Object} commande - Objet commande retourné par l'API (avec customer, order_items...)
 * @param {Function} [formaterDate] - Fonction optionnelle de formatage de la date de livraison
 * @returns {Object} Dictionnaire { cle: valeur } utilisable par appliquerVariables()
 */
export const construireVariablesCommande = (commande, formaterDate = null) => {
  if (!commande) return {};

  // Liste des articles sous forme de lignes "• 2 x Article — 10 000 F"
  const produits = (commande.order_items || [])
    .map((item) => {
      const nom =
        item.product_name ||
        item.product_variant?.product?.name ||
        item.product_variant?.title ||
        'Article';
      const total = (item.product_price || 0) * (item.quantity || 0);
      return `• ${item.quantity || 1} x ${nom} — ${formaterMontant(total)}`;
    })
    .join('\n');

  // Date de livraison : utilise le formateur fourni par l'appelant si présent
  let dateLivraison = 'à confirmer';
  if (commande.delivery_date) {
    dateLivraison = formaterDate
      ? formaterDate(commande.delivery_date)
      : commande.delivery_date;
  }

  return {
    client: commande.customer?.name || 'cher client',
    telephone: commande.customer?.phone || '',
    numero_commande: commande.order_number || '',
    montant: formaterMontant(commande.total_amount),
    adresse: commande.delivery_address || 'non renseignée',
    date_livraison: dateLivraison,
    produits: produits || 'Détail des articles disponible sur demande',
    livreur: commande.delivery_person?.name || 'notre livreur'
  };
};

export default TEMPLATES_MESSAGES_COMMANDE;
